/**
 * ModuleCatalogPage
 * Sökbar, editerbar översikt över alla moduler/funktioner i systemet, grupperade per system.
 * Texten (syfte/funktioner/krav-noteringar) lagras i databasen och används av AI för Skall/Börkrav.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Squares2X2Icon,
  ArrowLeftIcon,
  ArrowPathIcon,
  PlusIcon,
  ArrowDownTrayIcon,
  PencilSquareIcon,
  TrashIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { ROUTES } from '@/constants/routes';
import { useTranslation } from '@/store/languageStore';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/store/authStore';
import { toast } from 'react-hot-toast';
import {
  moduleCatalogService,
  type ModuleCatalogEntry,
  type CreateModuleCatalogEntryDto,
  type UpdateModuleCatalogEntryDto,
} from '@/services/crm/moduleCatalogService';
import ModuleCatalogEditModal from '@/components/crm/ModuleCatalogEditModal';
import ReleaseVersionDialog from '@/components/crm/ReleaseVersionDialog';
import { productReleaseApi } from '@/services/version/productReleaseApi';
import { downloadModuleCatalogPdf, splitFunctionItems } from '@/utils/moduleCatalogPdf';

function CatalogFunctions({
  text,
  label,
}: {
  text: string;
  label: string;
}) {
  const functionItems = splitFunctionItems(text);
  return (
    <div className="mt-1 text-sm text-textSecondary">
      <span className="font-medium">{label}:</span>
      {functionItems.length > 1 ? (
        <ul className="mt-1 list-disc pl-5 space-y-0.5">
          {functionItems.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : (
        <span> {text}</span>
      )}
    </div>
  );
}

const ModuleCatalogPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasFeatureAccess } = usePermissions();

  const isAdmin = user?.is_global_admin === true;
  const canEdit = isAdmin || hasFeatureAccess('CRM_MODULE_CATALOG') || hasFeatureAccess('CRM');

  const [entries, setEntries] = useState<ModuleCatalogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [versionFilter, setVersionFilter] = useState('');
  const [releaseOptions, setReleaseOptions] = useState<{ version: string; releasedOn: string }[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState<{ mode: 'edit' | 'create'; entry: ModuleCatalogEntry | null } | null>(null);
  const [releasing, setReleasing] = useState(false);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(handle);
  }, [search]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await moduleCatalogService.list(debouncedSearch || undefined);
      setEntries(data);
    } catch {
      toast.error(t('common.errorLoading') || 'Kunde inte hämta data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const loadReleases = () => {
    productReleaseApi.list()
      .then((data) => {
        setReleaseOptions(data.releases.map((release) => ({
          version: release.version,
          releasedOn: release.releasedOn,
        })));
      })
      .catch(() => {
        setReleaseOptions([]);
      });
  };

  useEffect(() => {
    loadReleases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const versionOptions = useMemo(() => {
    if (releaseOptions.length > 0) return releaseOptions;
    const seen = new Set<string>();
    const fromEntries: { version: string; releasedOn: string }[] = [];
    for (const entry of entries) {
      if (!entry.release_version || seen.has(entry.release_version)) continue;
      seen.add(entry.release_version);
      fromEntries.push({ version: entry.release_version, releasedOn: '' });
    }
    return fromEntries;
  }, [releaseOptions, entries]);

  const filteredEntries = useMemo(() => {
    if (!versionFilter) return entries;
    if (versionFilter === 'unreleased') return entries.filter((entry) => !entry.release_version);
    return entries.filter((entry) => entry.release_version === versionFilter);
  }, [entries, versionFilter]);

  const grouped = useMemo(() => {
    const map = new Map<string, { systemName: string; items: ModuleCatalogEntry[] }>();
    for (const e of filteredEntries) {
      const key = e.system_key;
      if (!map.has(key)) map.set(key, { systemName: e.system_display_name, items: [] });
      map.get(key)!.items.push(e);
    }
    for (const group of map.values()) {
      group.items.sort((a, b) => {
        const aDate = a.introduced_on ?? '9999-12-31';
        const bDate = b.introduced_on ?? '9999-12-31';
        if (aDate !== bDate) return aDate < bDate ? -1 : 1;
        return a.name.localeCompare(b.name, 'sv');
      });
    }
    return Array.from(map.entries()).sort((a, b) => a[1].systemName.localeCompare(b[1].systemName, 'sv'));
  }, [filteredEntries]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await moduleCatalogService.sync();
      toast.success(
        t('crm.moduleCatalog.syncDone', { inserted: res.inserted, updated: res.updated }) ||
          `Synkat: ${res.inserted} nya, ${res.updated} uppdaterade`
      );
      await load();
    } catch {
      toast.error(t('crm.moduleCatalog.syncError') || 'Synk misslyckades');
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveEdit = async (id: number, dto: UpdateModuleCatalogEntryDto) => {
    setSaving(true);
    try {
      await moduleCatalogService.update(id, dto);
      toast.success(t('common.saved') || 'Sparat');
      setModal(null);
      await load();
    } catch {
      toast.error(t('common.errorSaving') || 'Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCreate = async (dto: CreateModuleCatalogEntryDto) => {
    setSaving(true);
    try {
      await moduleCatalogService.create(dto);
      toast.success(t('common.saved') || 'Sparat');
      setModal(null);
      await load();
    } catch {
      toast.error(t('common.errorSaving') || 'Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry: ModuleCatalogEntry) => {
    if (!window.confirm(t('crm.moduleCatalog.confirmDelete') || 'Ta bort/inaktivera denna post?')) return;
    try {
      await moduleCatalogService.remove(entry.id);
      toast.success(t('common.deleted') || 'Borttagen');
      await load();
    } catch {
      toast.error(t('common.errorDeleting') || 'Kunde inte ta bort');
    }
  };

  const handleExportPdf = () => {
    if (grouped.length === 0) return;
    setExporting(true);
    try {
      const generatedDate = new Intl.DateTimeFormat(undefined, {
        dateStyle: 'long',
      }).format(new Date());
      downloadModuleCatalogPdf(
        grouped.map(([, group]) => group),
        {
          title: t('crm.moduleCatalog.title') || 'Modulkatalog',
          purpose: t('crm.moduleCatalog.purpose') || 'Syfte',
          functions: t('crm.moduleCatalog.functions') || 'Funktioner',
          unreleased: t('version.unreleased') || 'Ej i version',
          generated:
            t('crm.moduleCatalog.exportGenerated', { date: generatedDate }) ||
            generatedDate,
          pageOf: (page, pages) =>
            t('crm.moduleCatalog.exportPageOf', { page, pages }) || `${page} / ${pages}`,
        }
      );
    } catch {
      toast.error(t('crm.moduleCatalog.exportError') || 'Export misslyckades');
    } finally {
      setExporting(false);
    }
  };

  const btn = 'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors';

  return (
    <div className="p-4 bg-bgContainer rounded-lg h-full overflow-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(ROUTES.CRM)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
            <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
          </button>
          <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
            <Squares2X2Icon className="w-6 h-6 text-primary" />
            {t('crm.moduleCatalog.title') || 'Modulkatalog'}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canEdit && (
            <>
              {isAdmin && (
                <button onClick={() => setReleasing(true)} className={`${btn} border border-border text-textDefault hover:bg-bgElevated`}>
                  {t('version.releaseAction') || 'Släpp version'}
                </button>
              )}
              <button onClick={handleSync} disabled={syncing} className={`${btn} border border-border text-textDefault hover:bg-bgElevated disabled:opacity-50`}>
                <ArrowPathIcon className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                {t('crm.moduleCatalog.sync') || 'Synka'}
              </button>
              <button onClick={() => setModal({ mode: 'create', entry: null })} className={`${btn} border border-border text-textDefault hover:bg-bgElevated`}>
                <PlusIcon className="w-4 h-4" />
                {t('crm.moduleCatalog.addCustom') || 'Lägg till egen'}
              </button>
            </>
          )}
          <button onClick={handleExportPdf} disabled={exporting} className={`${btn} bg-primary text-white hover:opacity-90 disabled:opacity-50`}>
            <ArrowDownTrayIcon className="w-4 h-4" />
            {exporting ? t('common.exporting') || 'Exporterar...' : t('crm.moduleCatalog.exportPdf') || 'Exportera PDF'}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[16rem] max-w-md">
          <MagnifyingGlassIcon className="w-5 h-5 text-textSecondary absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('crm.moduleCatalog.searchPlaceholder') || 'Sök modul, syfte, funktion...'}
            className="w-full pl-10 pr-3 py-2 bg-bgElevated border border-border rounded-lg text-textDefault focus:border-primary focus:outline-none"
          />
        </div>
        <select
          value={versionFilter}
          onChange={(e) => setVersionFilter(e.target.value)}
          aria-label={t('crm.moduleCatalog.versionFilter') || 'Version'}
          className="px-3 py-2 bg-bgElevated border border-border rounded-lg text-textDefault focus:border-primary focus:outline-none"
        >
          <option value="">{t('crm.moduleCatalog.allVersions') || 'Alla versioner'}</option>
          {versionOptions.map((option) => (
            <option key={option.version} value={option.version}>
              v{option.version}{option.releasedOn ? ` · ${option.releasedOn}` : ''}
            </option>
          ))}
          {entries.some((entry) => !entry.release_version) && (
            <option value="unreleased">{t('version.unreleased') || 'Ej i version'}</option>
          )}
        </select>
      </div>

      {loading ? (
        <div className="text-textSecondary">{t('common.loading') || 'Laddar...'}</div>
      ) : grouped.length === 0 ? (
        <div className="text-textSecondary">{t('crm.moduleCatalog.empty') || 'Inga moduler hittades.'}</div>
      ) : (
        <div className="space-y-8">
          {grouped.map(([systemKey, group]) => (
            <section key={systemKey}>
              <h2 className="text-lg font-semibold text-textDefault mb-3 pb-1 border-b border-border">
                {group.systemName}
                <span className="ml-2 text-sm font-normal text-textSecondary">({group.items.length})</span>
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {group.items.map((e) => (
                  <div key={e.id} className="p-4 bg-bgElevated rounded-lg border border-border">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-semibold text-textDefault">{e.name}</h3>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {e.feature_code && (
                            <span className="text-xs px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                              {e.feature_code}
                            </span>
                          )}
                          {e.route && <span className="text-xs text-textSecondary">{e.route}</span>}
                          {e.system_key === 'mobilapp' && (
                            <span className="text-xs px-2 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300">
                              {t('crm.moduleCatalog.appBadge') || 'App'}
                            </span>
                          )}
                          {e.is_custom === 1 && (
                            <span className="text-xs px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                              {t('crm.moduleCatalog.customBadge') || 'Egen'}
                            </span>
                          )}
                          {e.introduced_on && (
                            <span className="text-xs text-textSecondary">{e.introduced_on}</span>
                          )}
                          {e.release_version ? (
                            <span className="text-xs px-2 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 font-mono">
                              v{e.release_version}
                            </span>
                          ) : (
                            <span className="text-xs px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                              {t('version.unreleased') || 'Ej i version'}
                              {' · '}
                              {t(`version.bump.${e.version_bump}`) || e.version_bump}
                            </span>
                          )}
                        </div>
                      </div>
                      {canEdit && (
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => setModal({ mode: 'edit', entry: e })} className="p-1.5 hover:bg-bgContainer rounded" title={t('common.edit') || 'Redigera'}>
                            <PencilSquareIcon className="w-4 h-4 text-textSecondary" />
                          </button>
                          <button onClick={() => handleDelete(e)} className="p-1.5 hover:bg-bgContainer rounded" title={t('common.delete') || 'Ta bort'}>
                            <TrashIcon className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      )}
                    </div>

                    {e.purpose && (
                      <p className="mt-2 text-sm text-textDefault">
                        <span className="font-medium">{t('crm.moduleCatalog.purpose') || 'Syfte'}: </span>
                        {e.purpose}
                      </p>
                    )}
                    {e.functions && (
                      <CatalogFunctions
                        text={e.functions}
                        label={t('crm.moduleCatalog.functions') || 'Funktioner'}
                      />
                    )}
                    {e.requirements_notes && (
                      <p className="mt-1 text-sm text-textSecondary italic">
                        <span className="font-medium">{t('crm.moduleCatalog.requirementsNotes') || 'Krav'}: </span>
                        {e.requirements_notes}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {modal && (
        <ModuleCatalogEditModal
          entry={modal.entry}
          mode={modal.mode}
          saving={saving}
          onClose={() => setModal(null)}
          onSaveEdit={handleSaveEdit}
          onSaveCreate={handleSaveCreate}
          canSetBump={isAdmin && modal.entry != null && modal.entry.release_id == null}
        />
      )}

      {releasing && (
        <ReleaseVersionDialog
          onClose={() => setReleasing(false)}
          onReleased={() => {
            setReleasing(false);
            load();
            loadReleases();
          }}
        />
      )}
    </div>
  );
};

export default ModuleCatalogPage;
